import { IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { AddOnDraft } from "../../types";
import { emptyAddOn } from "../../helpers";

export interface AddOnsSectionProps {
  addOns: AddOnDraft[];
  onChange: (addOns: AddOnDraft[]) => void;
}

/**
 * The extras priced on top of the charger and installation — a permit, a panel
 * upgrade, a longer run. Free text so the vendor can quote one-offs that were
 * never published on their service offering.
 */
const AddOnsSection: React.FC<AddOnsSectionProps> = ({ addOns, onChange }) => {
  const theme = useTheme();
  const { t } = useTranslation("leads");

  const update = (key: string, patch: Partial<AddOnDraft>) =>
    onChange(
      addOns.map((addOn) =>
        addOn.key === key ? { ...addOn, ...patch } : addOn,
      ),
    );

  const remove = (key: string) =>
    onChange(addOns.filter((addOn) => addOn.key !== key));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
      }}
    >
      <Typography variant="subtitle2">{t("revise.add_ons")}</Typography>

      {addOns.length === 0 && (
        <Typography
          variant="body2"
          style={{ color: theme.palette.text.secondary }}
        >
          {t("revise.no_add_ons")}
        </Typography>
      )}

      {addOns.map((addOn) => (
        <div
          key={addOn.key}
          style={{
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 2 }}>
            <TextField
              name={`addOnLabel-${addOn.key}`}
              label={t("revise.add_on")}
              value={addOn.label}
              placeHolder="e.g. 200A panel upgrade"
              onChange={(e) => update(addOn.key, { label: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              name={`addOnPrice-${addOn.key}`}
              label={t("revise.price")}
              type="number"
              value={addOn.price}
              onChange={(e) => update(addOn.key, { price: e.target.value })}
            />
          </div>
          <IconButton
            aria-label={t("revise.remove_add_on")}
            onClick={() => remove(addOn.key)}
            style={{ marginTop: theme.spacing(1) }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}

      <div>
        <Button
          size="small"
          startAdornment={<AddIcon />}
          onClick={() => onChange([...addOns, emptyAddOn()])}
        >
          {t("revise.add_add_on")}
        </Button>
      </div>
    </div>
  );
};

export default AddOnsSection;
